import APIClient, { failSafe, makeCancellableAPIRequest, showAPIError } from './index';
import CustomDashboardAPI from './CustomDashboardAPI';
import GlobalSettingAPI from './GlobalSettingAPI';

const ProductAPI = {
    getTypes(){
        const { promise, cancel, requestInfo } =  makeCancellableAPIRequest(
            'GET',
            '/user/product-type'
        );
        return { promise, cancel, requestInfo };
    },
    createType(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/product-type',
            {
                data
            }
        )
    },
    updateType(data){
        return makeCancellableAPIRequest(
            'PUT',
            '/user/product-type',
            {
                data
            }
        )
    },
    deleteType(id){
        return  makeCancellableAPIRequest(
            'DELETE',
            `/user/product-type/${id}`,
            {},
            false
        )
    },
    orderType(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/product-type/order',
            {
                data
            }
        )
    },
    async save(data){
        try{
            const response = await APIClient.post('/user/product', {
                ...data,
            });
            return response.data;
        } catch (e){
            console.log(e);
            showAPIError(e);
            throw e;
        }
    },
    getAll(){
        console.warn("'ProductAPI.getAll' is depricated, use 'ProductAPI.v2.getAll'");
        return failSafe(
            APIClient.get('/user/product')
        );
    },
    get(id, queryParams = {}){
        return makeCancellableAPIRequest(
            'GET',
            `/user/product/${id}?ignoreSubProducts=true`,
            { params: queryParams }
        );
    },
    update(data){
        return failSafe(
            APIClient.put('/user/product/', {
                ...data,
            })
        );
    },
    delete(id) {
        return failSafe(
            APIClient.delete(`/user/product/${id}`)
        );
    },
    archiveDelete(id, comment) {
        return failSafe(
            APIClient.post(`/user/product/archive/${id}?comment=${comment}&delete=true`)
        )
    },
    getProductByBu(businessUnitId, id) {
        return makeCancellableAPIRequest(
            'GET',
            `/user/product/${businessUnitId}/${id}`
        );
    },
    v2: {
        getAll(params={}){
            return makeCancellableAPIRequest(
                'GET',
                '/user/product/elastic',
                {
                    params
                }
            )
        },
        get(id){
            return makeCancellableAPIRequest(
                'GET',
                `/user/product/elastic/${id}`
            )
        },
        getPaginatedAll(data){
            return makeCancellableAPIRequest(
                'POST',
                '/user/product/elastic/paged',
                {
                    data
                }
            )
        },
    },
    getFilter(){
        return failSafe(
            APIClient.get('/user/product/filter')
        )
    },
    postFilter(data){
        return makeCancellableAPIRequest(
            'POST',
            '/user/product/filter',
            {data}
        )
    },
    getGlobalFilter() {
        return failSafe(
            APIClient.get('/user/product/global/filter/')
        )
    },
    getAllQuick(BUAggregation = false){
        const path = BUAggregation ? '/api/businessunits/dashboard/product/elastic/short' : '/user/product/elastic/short'
        return makeCancellableAPIRequest(
            'GET',
            path
        )
    },
    getAllQuick_V2(data={}){
        return makeCancellableAPIRequest(
            'POST',
            'api/product/short',
            {data}
        )
    },
    getAllProductsSpecificFields(fields = [], filters, buIds){
        if(!fields.includes('id')){
            fields.push('id');
        }
        if(!fields.includes('name')){
            fields.push('name');
        }
        return makeCancellableAPIRequest(
            'POST',
            '/user/product/dynamic',
            {
                data: { fields, filters, buIds }
            }
        )
    },
    getByBU(buId){
        return makeCancellableAPIRequest(
            'GET',
            `/api/tenant/businessUnits/${buId}/products`
        )
    },
    getAllProductsByBU(buId, params = {}){
        const finalParams = {
            fetchTags: false,
            ...params
        }
        return makeCancellableAPIRequest(
            'GET',
            `/user/product/elastic/business-unit/${buId}`,
            {
                params: finalParams
            }
        )
    },
    getFilteredProductsByBU(buId){
        return makeCancellableAPIRequest(
            'GET',
            `/user/product/elastic/business-unit/filtered/${buId}`
        )
    },
    validate(params = {}, showDefaultMessage = false){
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/validate', 
            {
                params
            },
            showDefaultMessage
        )
    },
    getHallOfFame(params){
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/elastic/products/hall-of-fame/',
            {
                params
            }
        )
    },
    getHallOfFameByTeam(params){
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/elastic/products/hall-of-fame/by-team/',
            {
                params
            }
        )
    },
    getAppSecStatus(params){
        return makeCancellableAPIRequest(
            'GET',
            '/user/tools/appsec-tools/status-by-product',
            {
                params
            }
        )
    },
    getAppSecStatusv2(restParams){
        return makeCancellableAPIRequest(
            'POST',
            '/user/tools/appsec-tools/status-aggregated-by',
            {
                data: {
                    aggregateField: 'PRODUCT',
                    ...restParams
                }
            }
        )
    },
    getFindingSLADetails(params) {
        return makeCancellableAPIRequest(
            'GET',
            'user/findingSla/sla-template',
            {
                params
            }
        )
    },
    getAggregatedByTeam() {
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/get-aggreagted-data-by-team'
        )
    },
    getAggregatedByTeamNew() {
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/get-aggreagted-data?aggType=TEAM'
        )
    },
    getAggregatedByTier() {
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/get-aggreagted-data?aggType=TIER'
        )
    },
    getProductDependencyList(id, params) {
        return makeCancellableAPIRequest(
            'GET',
            '/user/product/get-mapping/' + id,
            {
                params
            }
        )
    },
    deleteProductDependency(data){
        const { productId, subProductId } = data;
        return failSafe(
            APIClient.delete(`/user/product/remove-mapping/${productId}/${subProductId}`)
        )
    },
    updateProductDependency(data){
        const { productId, subProductId, considerRiskScore } = data;
        return makeCancellableAPIRequest(
            'PUT',
            `/user/product/add-mapping/${productId}/${subProductId}/${considerRiskScore}`
        )
    },
    importCSV(data, isEdit){
        return makeCancellableAPIRequest(
            isEdit ? 'PUT' : 'POST',
            '/user/product/upload',
            {
                data,
                headers: {
                    'content-type': 'multipart/form-data'
                }
            },
        )
    },
    moveToAnotherBusinessUnit(productId, newBuId){
        return makeCancellableAPIRequest(
            'POST',
            `/user/product/${productId}/move/${newBuId}`
        )
    },
    archiveProduct(productId, note){
        return makeCancellableAPIRequest(
            'POST',
            `/user/product/archive/${productId}?comment=${note}`
        )
    },
    downloadCsvV2(queryParams = {}){
        return makeCancellableAPIRequest(
            'POST',
            '/api/products/export',
            {
                data: queryParams,
            }
        )
    },
    bulkUpdate(ids, payload){
        return makeCancellableAPIRequest(
            'PATCH',
            '/user/product/bulk',
            {
                data: {
                    ids,
                    ...payload
                },
            }
        )
    },
    bulkMove(ids, buId) {
        return makeCancellableAPIRequest(
            'POST',
            '/api/products/bulk/move',
            {
                data: {
                    'productIds': ids,
                    'targetBusinessUnitId': buId
                }
            }
        )
    },
    bulkDelete(ids) {
        return makeCancellableAPIRequest(
            'DELETE',
            '/api/products/bulk',
            {
                data: {
                    'productIds': ids,
                }
            }
        )
    },
    bulkArchive(ids, note, shouldDelete = false) {
        return makeCancellableAPIRequest(
            'POST',
            '/api/products/bulk/archive',
            {
                data: {
                    'productIds': ids,
                    'comment': note,
                    delete: shouldDelete
                }
            }
        )
    },
    getProductsByGroup({ buId, groups }){
        return makeCancellableAPIRequest(
            'POST',
            'user/sub-product/elastic/products-by-group',
            {
                data: {
                    buId, groups
                }
            }
        );
    },
    getPaginatedProductsByHierarchy(params, data){
        return makeCancellableAPIRequest(
            'POST',
            '/api/v2/product/products-by-group',
            {
                params,
                data
            }
        );
    },
    createVersion({ productId, version, cloneManualFindings, status }){
        return makeCancellableAPIRequest(
            'POST',
            '/api/product/version',
            {
                data: {
                    productId,
                    version,
                    cloneManualFindings,
                    status
                }
            }
        )
    },
    createBulkVersion({ productIds, version, cloneManualFindings, status }){
        return makeCancellableAPIRequest(
            'POST',
            '/api/product/bulk/versions',
            {
                data: {
                    productIds,
                    version,
                    cloneManualFindings,
                    status
                }
            }
        )
    },
    getProductBulkPreviewVersions({ productIds, version, cloneManualFindings }){
        return makeCancellableAPIRequest(
            'POST',
            '/api/product/bulk/preview/versions',
            {
                data: {
                    productIds,
                    version,
                    cloneManualFindings
                }
            }
        )
    },
    getTeamsByProduct(productId) {
        return makeCancellableAPIRequest(
            'GET',
            `/api/team/search/${productId}`
        );
    },
    updateOtherProperties(productId, data) {
        return makeCancellableAPIRequest(
            'PATCH',
            `/api/product/${productId}/properties`,
            { data }
        );
    },
    getDefaultProductSubproductDashboard() {
        const dashboardsRequest = CustomDashboardAPI.getAllPublicDashbards();
        const customizedDefaultRequest = GlobalSettingAPI.getCustomizeDefaultDashboards();

        const _promise = Promise.all([dashboardsRequest.promise, customizedDefaultRequest.promise])
            .then(([dashboards, customizedDefaultDashboardsData]) => {
                const findDashboard = (dashboardId) => {
                    if (!dashboardId) return undefined;
                    return dashboards.find((dashboard) => dashboard.id === dashboardId);
                };

                const primaryDashboardData = findDashboard(customizedDefaultDashboardsData?.primaryDashboard);
                const secondaryDashboardData = findDashboard(customizedDefaultDashboardsData?.secondaryDashboard);

                return {
                    primaryDashboard: primaryDashboardData && {
                        id: primaryDashboardData?.id,
                        name: primaryDashboardData?.name,
                    },
                    secondaryDashboard: secondaryDashboardData && {
                        id: secondaryDashboardData?.id,
                        name: secondaryDashboardData?.name,
                    },
                };
        });

        return {
                cancel: () => {
                    dashboardsRequest.cancel();
                    customizedDefaultRequest.cancel();
                },
                promise: _promise,
        };
    }, 
    getHierarchyFilterOptions(fields = []) {
        return makeCancellableAPIRequest(
            'POST',
            '/api/v2/product/filter-options',
            {
                data: { fields }
            }
        );
    },
}

export default ProductAPI;
